import {
  Component,
  ChangeDetectionStrategy,
  Input,
  ElementRef,
  ViewChild,
  OnChanges,
  SimpleChanges,
  EventEmitter,
  Output,
} from '@angular/core';
import { AttachmentFileType, AttachmentUpload, UploadProgress } from '../../interface';
import { AttachmentStatus } from '../../const';
import cancelUpload1 from './images/cancel_upload.svg?raw';
import uploadError1 from './images/upload_error.svg?raw';

@Component({
  selector: 'skypro-message-uploaded-attachment',
  templateUrl: 'uploaded-attachment-card.html',
  styleUrls: ['uploaded-attachment-card.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class UploadedAttachmentCardComponent implements OnChanges {
  @Input() attachment: AttachmentUpload;
  @Input() progress: UploadProgress;

  @Output() removeAttachment = new EventEmitter<void>();

  @ViewChild('progressCircle') progressCircle!: ElementRef<HTMLElement>;

  attachmentStatus = AttachmentStatus;
  attachmentFileType = AttachmentFileType;

  progressValue = 0;
  progressValueInPercent = 0;
  maxProgress = 100;
  progressCircleRadius = 18;
  progressCircumference = 2 * Math.PI * this.progressCircleRadius;

  icons = {
    cancelUpload: cancelUpload1,
    uploadError: uploadError1,
  };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['progress'] && this.progress?.uuid === this.attachment.uuid) {
      this.updateProgressSmoothly(this.progress.value);
    }
  }

  remove(): void {
    this.removeAttachment.emit();
  }

  private updateProgressSmoothlyValues(value: number): void {
    this.progressValue = this.valueInRange(this.progressValue, this.maxProgress, 0) + 1;

    const passedValue = this.toPercent(value, this.maxProgress);
    const progressValue = this.toPercent(this.progressValue, this.maxProgress);

    if (this.progressValueInPercent === passedValue || progressValue > passedValue) {
      this.progressValue = this.toPercent(value, this.maxProgress);
      this.progressValueInPercent = this.toPercent(this.progressValue, this.maxProgress);
    } else {
      this.progressValueInPercent = progressValue;
      requestAnimationFrame(() => this.updateProgressSmoothly.call(this, value));
    }
  }

  private getProgress(value: number): number {
    return this.progressCircumference - (value * this.progressCircumference) / 100;
  }

  /** value [0..100] */
  private updateProgressSmoothly(value: number): void {
    const frames = [
      {
        strokeDashoffset: this.getProgress(this.progressValue),
      },
      {
        strokeDashoffset: this.getProgress(this.progressValueInPercent),
      },
    ];

    if (this.progressCircle !== undefined) {
      this.progressCircle.nativeElement.animate(frames, {
        easing: 'ease-out',
        fill: 'forwards',
      });
      this.updateProgressSmoothlyValues(value);
    }
  }

  private valueInRange(value: number, max: number, min = 0): number {
    return Math.max(Math.min(value, max), min);
  }

  private toPercent(value: number, max: number): number {
    return Math.floor((100 * value) / max);
  }
}
